import React, {ComponentProps, useMemo} from 'react'

import useSearch from '../../../../commons/src/hooks/use-search'
import {Filter, FilterParams, StringMatchingFilter, getCount} from '../../libs/table-row-filter'
import {createStringMatchingOptionsSearch} from '../../services/search'

import Button from '../../../../commons/src/components/button'
import FilterSelect from '../../../../commons/src/components/filter-select'
import FilterSelectFlyout from '../../../../commons/src/components/filter-select-flyout'
import LabeledCheckbox from '../../../../commons/src/components/labeled-checkbox'
import SearchInputField from '../../../../commons/src/components/search-input-field'

type FilterSelectProps = ComponentProps<typeof FilterSelect>
export type FilterOption = {id: string; label: React.ReactNode; searchable: string}

type Props<TableRow> = {
  label: string
  options: FilterOption[]
  filterKey: string
  filterMap: Record<string, Filter<TableRow, FilterParams<TableRow>>>
  tableRows: TableRow[]
  applyFilter: (newFilter: StringMatchingFilter<TableRow>) => void
  onClear?: () => void
  showSearch?: boolean
  showCount?: boolean
  clearLabel?: string
  size?: FilterSelectProps['size']
}

const StringMatchingFilterPartial = <TableRow extends unknown>({
  label,
  options,
  filterKey,
  filterMap,
  tableRows,
  applyFilter,
  onClear,
  showSearch = false,
  showCount = true,
  clearLabel = 'Clear',
  size = '100',
}: Props<TableRow>) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const filter = filterMap[filterKey] as StringMatchingFilter<TableRow>

  const [filterValues, setFilterValues] = React.useState<string[]>(filter.serialize().filterValues)

  const getCountFor = (newFilterValues: string[]) => {
    const newFilter = new StringMatchingFilter({
      ...filter.serialize(),
      filterValues: newFilterValues,
    })
    return getCount(tableRows, filterMap, filterKey, newFilter)
  }

  const memoizedCounts = useMemo(() => {
    if (showCount) {
      return options.reduce((acc: Record<string, number>, option: FilterOption) => {
        acc[option.id] = getCountFor([option.id])
        return acc
      }, {})
    }
    return {}
  }, [options, tableRows, filterMap])

  const {searchResults, setSearchValue, searchValue} = useSearch(
    options,
    createStringMatchingOptionsSearch,
    !showSearch
  )

  React.useEffect(() => {
    if (isOpen) {
      setFilterValues(filter.serialize().filterValues)
    }
  }, [isOpen])

  const handleClear = () => {
    if (onClear) {
      onClear()
    } else {
      applyFilter(
        new StringMatchingFilter({
          ...filter.serialize(),
          filterValues: [],
        })
      )
      setFilterValues([])
    }
    setIsOpen(false)
    setSearchValue('')
  }

  const handleSubmit = () => {
    applyFilter(
      new StringMatchingFilter({
        ...filter.serialize(),
        filterValues,
      })
    )
    setIsOpen(false)
    setSearchValue('')
  }

  return (
    <FilterSelect
      label={filter.getLabel(label)}
      size={size}
      active={filter.isActive()}
      isOpen={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      onShouldClose={() => setIsOpen(false)}
      menu={
        <FilterSelectFlyout
          onClear={handleClear}
          clearLabel={clearLabel}
          headerContent={
            showSearch && (
              <SearchInputField
                size="90"
                name="string-matching-search"
                placeholder="Search…"
                value={searchValue}
                onChange={value => {
                  setSearchValue(value)
                }}
              />
            )
          }
          submitButton={
            <Button
              block
              onClick={() => {
                handleSubmit()
              }}
            >
              Apply{showCount && `( ${getCountFor(filterValues)})`}
            </Button>
          }
        >
          {searchResults.map((option, index) => (
            <LabeledCheckbox
              key={option.id}
              label={option.label}
              checked={filterValues.includes(option.id)}
              count={showCount ? memoizedCounts[option.id] : undefined}
              onChange={value => {
                if (value) {
                  setFilterValues([...filterValues, option.id])
                } else {
                  setFilterValues(
                    filterValues.filter((filterValue: string) => filterValue !== option.id)
                  )
                }
              }}
              compact
            />
          ))}
        </FilterSelectFlyout>
      }
    />
  )
}

export default StringMatchingFilterPartial
